import { useRateFieldError } from "../../hooks/useRateFieldError";
import { FC } from "react";

import { TicketingFormCategory } from "@simplyk/common";
import { useFormContext } from "react-hook-form";

import { useRateContext } from "../../Context/RateContext";
import { useRateFieldName } from "../../hooks/useRateFieldName";

import { isMax, isMin, isRequired } from "@/common/helpers/validators";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardInformationTooltip } from "@/dashboard-design-system/DashboardTooltip/DashboardInformationTooltip";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { StepTicketsFormData } from "@/features/LiveFormEditor/Steps/StepTickets/types";

interface MaxPerOrderFieldProps {
  label?: string;
  tooltip?: string;
}

export const MaxPerOrderField: FC<MaxPerOrderFieldProps> = ({ label: labelProp, tooltip: tooltipProp }) => {
  const { t } = useTranslate();
  const { formCategory, isRegistration, isSweepstakes } = useRateContext();
  const { control } = useFormContext<StepTicketsFormData>();

  const fieldName = useRateFieldName("rateMaximumNumberToBuy");
  const error = useRateFieldError("rateMaximumNumberToBuy");

  const isShop = formCategory === TicketingFormCategory.Shop;
  const tooltipKey = isShop
    ? "ticketing.rateMaximumNumberToBuyInformationsShop"
    : isRegistration
      ? "ticketing.rateMaximumNumberToBuyInformationsRegistration"
      : "ticketing.rateMaximumNumberToBuyInformations";
  const tooltip = isSweepstakes
    ? t("dashboard", "sweepstakes.rates.maxPerParticipationTooltip")
    : t("dashboard", tooltipKey);

  return (
    <DashboardTextField
      label={
        <span>
          <DashboardTypography variant="body2" sx={{ display: "inline", mr: 0.5 }}>
            {labelProp ??
              t(
                "dashboard",
                isSweepstakes ? "sweepstakes.rates.maxPerParticipation" : "ticketing.rateMaximumNumberToBuy"
              )}
          </DashboardTypography>
          <DashboardInformationTooltip title={tooltipProp ?? tooltip} placement="top" alignOnText />
        </span>
      }
      name={fieldName}
      defaultValue={10}
      control={control}
      size="medium"
      errorMessage={error?.message}
      rules={[
        isMin(1, t("dashboard", "ticketing.rateMaximumNumberToBuyMinimum")),
        isMax(100, t("dashboard", "ticketing.rateMaximumNumberToBuyMaximum")),
        isRequired(t("dashboard", "ticketing.rateMaximumNumberToBuyIsRequired")),
      ]}
      type="number"
    />
  );
};
